import * as React from "react";
import { BarChart3, Calculator, Home, Construction, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";

interface Indicador {
  title: string;
  description: string;
  url: string;
  icon: React.ElementType;
}

const INDICADORES: Indicador[] = [
  {
    title: "Inflação",
    description: "Calculadora pela Inflação (IPCA, IGP-M, INCC-DI, INPC)",
    url: "https://brasilindicadores.com.br/inflacao/calculadora/",
    icon: BarChart3,
  },
  {
    title: "IGP-M",
    description: "Calculadora de correção de valores pelo IGP-M",
    url: "https://brasilindicadores.com.br/igpm/calculadora/",
    icon: BarChart3,
  },
  {
    title: "IVAR",
    description: "Calculadora de correção de valores pelo IVAR",
    url: "https://brasilindicadores.com.br/ivar/calculadora/",
    icon: Home,
  },
  {
    title: "IPCA",
    description: "Calculadora de correção de valores pelo IPCA",
    url: "https://brasilindicadores.com.br/ipca/calculadora/",
    icon: Calculator,
  },
  {
    title: "INCC",
    description: "Calculadora de correção de valores pelo INCC",
    url: "https://brasilindicadores.com.br/incc-m/calculadora/",
    icon: Construction,
  },
];

interface IndicadoresSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const IndicadoresSelector = ({ open, onOpenChange }: IndicadoresSelectorProps) => {
  const isMobile = useIsMobile();

  const handleSelect = (url: string) => {
    onOpenChange(false);
    window.setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
    }, 0);
  };

  const Content = (
    <div className="space-y-2 sm:space-y-3 py-2 overflow-y-auto max-h-[60vh]">
      {INDICADORES.map((indicador) => {
        const IconComponent = indicador.icon;
        return (
          <button
            key={indicador.title}
            onClick={() => handleSelect(indicador.url)}
            className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors text-left"
          >
            <div className="p-2 sm:p-3 rounded-xl bg-amber-500/20 shrink-0">
              <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="font-medium text-sm sm:text-base">{indicador.title}</p>
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{indicador.description}</p>
            </div>
            <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        );
      })}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-lg sm:text-xl font-semibold">Calculadora de Indicadores</DrawerTitle>
            <DrawerDescription className="pt-1 text-sm">
              Selecione o indicador para correção de valores
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 overflow-y-auto flex-1">{Content}</div>
          <DrawerFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-full h-12 sm:h-14"
            >
              Fechar
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">Calculadora de Indicadores</DialogTitle>
          <DialogDescription>
            Selecione o indicador para correção de valores
          </DialogDescription>
        </DialogHeader>
        {Content}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full h-12"
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
